import { DateTime } from 'luxon';
import { PostingLoadDTO } from '../../data/dto/PostingLoadDTO';
import {
  formatMonthDay,
  getStepRuleText,
  isPickupOpen,
  StepName,
  toDate,
} from './postingDateText';

export type StepKey = 'pickup' | 'delivery';

export const STEP_NAMES: Record<StepKey, StepName> = {
  pickup: 'Pickup',
  delivery: 'Delivery',
};

interface DateRuleContext {
  load: PostingLoadDTO;
  isBooking: boolean;
  today: DateTime;
  /** The pickup day currently chosen in the form, if any. */
  pickupDate?: DateTime | null;
}

export function getDateFieldRules(
  stepKey: StepKey,
  { load, isBooking, today, pickupDate }: DateRuleContext,
) {
  const step = load[stepKey];
  const limit = toStartOfDay(step.scheduled_at);
  const pickupStart = toStartOfDay(load.pickup.scheduled_at);
  // Exact and No later than are skipped once the pickup is past due,
  // unless the carrier books now. No earlier than is always enforced.
  const isExpiryEnforced = isBooking || !pickupStart || pickupStart >= today;

  // A shipper date already in the past can't be met. Picking a day outside
  // the window is how Book Now becomes a counter-offer, so don't block it.
  const isLimitReachable = !!limit && limit >= today;
  const isExactEnforced =
    isExpiryEnforced && step.date_type === 'exact' && isLimitReachable;

  const tintEnd =
    toDate(step.scheduled_ends_at)?.endOf('day') ?? limit?.endOf('day');

  function getFooterRule() {
    const text = getStepRuleText(STEP_NAMES[stepKey], step);
    if (!text || !isPickupOpen(load.pickup, today)) return null;

    return { text, hasBlueBox: step.date_type === 'estimated' };
  }

  const deliveryStep = load.delivery;
  const deadline =
    stepKey === 'pickup' ? toStartOfDay(deliveryStep.scheduled_at) : undefined;

  return {
    // A pickup after the delivery deadline leaves no valid delivery day.
    // Same expiry rule as the delivery picker: relaxed once pickup is past due.
    getDeadlineError(day: DateTime): string | null {
      if (!isExpiryEnforced || !deadline || deadline < today) return null;
      if (day.startOf('day') <= deadline) return null;

      if (deliveryStep.date_type === 'not_later_than') {
        return `Pickup can't be after the delivery deadline (${formatMonthDay(deadline)})`;
      }
      if (deliveryStep.date_type === 'exact') {
        return `Pickup can't be after the delivery date (${formatMonthDay(deadline)})`;
      }

      return null;
    },
    // Same visibility as load details: only "Available Now" shows no rule.
    footerRule: getFooterRule(),
    // Only Estimated is tinted; the other types disable days instead.
    tintRange:
      step.date_type === 'estimated' && limit && tintEnd
        ? ([limit, tintEnd] as const)
        : null,
    lockedDate: isExactEnforced ? (limit ?? null) : null,
    getDisabledReason(day: DateTime): string | null {
      if (day < today) return 'Past date';

      const chosenPickup = pickupDate?.startOf('day');
      if (stepKey === 'delivery' && chosenPickup && day < chosenPickup) {
        return `Can't be before pickup (${formatMonthDay(chosenPickup)})`;
      }

      if (step.date_type === 'not_earlier_than' && limit && day < limit) {
        return `Shipper needs this no earlier than ${formatMonthDay(limit)}`;
      }

      if (
        isExpiryEnforced &&
        isLimitReachable &&
        step.date_type === 'not_later_than' &&
        limit &&
        day > limit
      ) {
        return `Shipper needs this no later than ${formatMonthDay(limit)}`;
      }

      if (isExactEnforced && limit && !day.hasSame(limit, 'day')) {
        return `Shipper set an exact date: ${formatMonthDay(limit)}`;
      }

      return null;
    },
  };
}

function toStartOfDay(value: string | null | undefined) {
  return toDate(value)?.startOf('day');
}

const DEFAULT_PICKER_HOUR = 9;

export function getDefaultPickerTime() {
  return DateTime.fromObject({
    hour: DEFAULT_PICKER_HOUR,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}

export function setDefaultPickerHour(date: DateTime) {
  return date.set({ hour: DEFAULT_PICKER_HOUR });
}
